import Tooltip from 'deprecated/atoms/tooltip';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { pointerDirection } from 'types';

interface SimpleTooltipPropsInterface {
  children: JSX.Element | JSX.Element[];
  placement?: pointerDirection;
  position?: { top?: number; left?: number; bottom?: number; right?: number };
  theme?: string;
  width: number;
  height: number;
  toolTipOffsets?: {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
  };
  isShown: boolean;
  pointerPlacement?: boolean;
  onOutsideClick?: (e: any) => any;
}

interface SimpleTooltipStateInterface {
  placement: pointerDirection;
  pointerPosition?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
  position: { left?: number; right?: number; top?: number; bottom?: number };
}

const toolTipRoot = document.getElementById('tooltip-root');
const TIP_HEIGHT = 10;

export default class TriggeredTooltip extends React.Component<
  SimpleTooltipPropsInterface,
  SimpleTooltipStateInterface
> {
  constructor(props) {
    super(props);
    this.state = {
      placement: this.props.placement,
      position: null,
    };
  }

  private el = document.createElement('div');
  private toolTipRef = React.createRef<HTMLDivElement>();

  public componentDidMount() {
    toolTipRoot.appendChild(this.el);

    const { width, height } = this.toolTipRef.current.getBoundingClientRect();

    this.setState(
      {
        position: this.calculatePosition(width, height),
      },
      () =>
        this.setState({
          pointerPosition: this.calculatePointerPlacement(width, height),
        }),
    );

    window.addEventListener('click', this.handleOutsideClick);
  }

  public componentWillUnmount() {
    // Remove the element from the DOM when we unmount
    toolTipRoot.removeChild(this.el);
    window.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick = e => {
    const { onOutsideClick } = this.props;

    if (
      !toolTipRoot.contains(e.target) &&
      onOutsideClick &&
      this.props.isShown
    ) {
      onOutsideClick(e.target);
    }
  };

  private calculatePosition = (width: number, height: number) => {
    const { left, top } = this.props.position;
    const { placement } = this.state;

    const {
      left: oLeft = 0,
      right: oRight = 0,
      top: oTop = 0,
      bottom: oBottom = 0,
    } = this.props.toolTipOffsets || {};

    if (placement === 'left') {
      const leftVal = left - width - TIP_HEIGHT + oLeft + window.pageXOffset;

      if (leftVal <= 0) {
        this.setState({
          placement: 'right',
        });

        return {
          left:
            left + TIP_HEIGHT + this.props.width + oRight + window.pageXOffset,
          top:
            top -
            height / 2 +
            this.props.height / 2 +
            oTop +
            window.pageYOffset,
        };
      }

      return {
        left: left - width - TIP_HEIGHT + oLeft + window.pageXOffset,
        top:
          top - height / 2 + this.props.height / 2 + oTop + window.pageYOffset,
      };
    }

    if (placement === 'top') {
      const leftVal =
        left - width / 2 + this.props.width + oLeft + window.pageXOffset;

      if (leftVal <= 0) {
        this.setState({
          placement: 'right',
        });
        return {
          left:
            left + TIP_HEIGHT + this.props.width + oRight + window.pageXOffset,
          top:
            top -
            height / 2 +
            this.props.height / 2 +
            oTop +
            window.pageYOffset,
        };
      }

      return {
        left: leftVal,
        top:
          top -
          height -
          this.props.height -
          TIP_HEIGHT / 4 +
          oTop +
          window.pageYOffset,
      };
    }

    if (placement === 'bottom') {
      const leftVal =
        left - width / 2 + this.props.width + oLeft + window.pageXOffset;

      if (leftVal <= 0) {
        this.setState({
          placement: 'right',
        });

        return {
          left:
            left + TIP_HEIGHT + this.props.width + oRight + window.pageXOffset,
          top:
            top -
            height / 2 +
            this.props.height / 2 +
            oTop +
            window.pageYOffset,
        };
      }
      return {
        left: leftVal,
        top:
          top + this.props.height + TIP_HEIGHT + oBottom + window.pageYOffset,
      };
    }

    if (placement === 'right') {
      return {
        left:
          left + TIP_HEIGHT + this.props.width + oRight + window.pageXOffset,
        top:
          top - height / 2 + this.props.height / 2 + oTop + window.pageYOffset,
      };
    }
  };

  private calculatePointerPlacement = (width, height) => {
    const { placement } = this.state;

    const { left: oLeft = 0, right: oRight = 0, top: oTop = 0 } =
      this.props.toolTipOffsets || {};

    if (placement === 'left') {
      return {
        right: 0,
        top: height / 2 - oTop,
      };
    }

    if (placement === 'right') {
      return {
        left: 0,
        top: height / 2 - oTop,
      };
    }

    if (placement === 'bottom') {
      return {
        left: width / 2 - TIP_HEIGHT - this.props.width / 2 - oLeft + oRight,
        top: -(TIP_HEIGHT * 2),
      };
    }

    if (placement === 'top') {
      return {
        bottom: 0,
        left: width / 2 - TIP_HEIGHT - this.props.width / 2 - oLeft + oRight,
      };
    }
  };

  public render() {
    const { children, theme, pointerPlacement } = this.props;
    const { placement = 'bottom' } = this.state;

    const { width = 0, height = 0 } =
      (this.toolTipRef &&
        this.toolTipRef.current &&
        this.toolTipRef.current.getBoundingClientRect()) ||
      {};
    const position = this.calculatePosition(width, height);
    const pointerPosition = this.calculatePointerPlacement(width, height);

    return ReactDOM.createPortal(
      <div
        style={{
          zIndex: 900,
        }}
      >
        <Tooltip
          isShown
          placement={placement}
          fixedLocation={{ ...position }}
          theme={theme}
          ref={this.toolTipRef}
          pointerPlacement={pointerPlacement ? pointerPosition : null}
        >
          {children}
        </Tooltip>
      </div>,
      // A DOM element
      this.el,
    );
  }
}
